import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle2, FileSearch, Sparkles, BrainCircuit, ArrowRight } from 'lucide-react';
import { updateDoc, serverTimestamp, doc, getDoc, increment } from 'firebase/firestore';
import { httpsCallable } from '../lib/callable';
import { functions, db } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { projectDoc } from '../constants/firestorePaths';
import StudySetupForm from './StudySetupForm';
import { StudySetup, DEFAULT_STUDY_SETUP } from '../constants/studySetupOptions';
import { functionsBase } from '../config/appConfig';
import { autoFilledProtocolNumber } from '../utils/protocolNumberRule';
import { protocolReadFailureMessage } from '../utils/protocolReadFailure';
import { countriesPrefill } from '../utils/protocolCountriesRule';

interface ProtocolUploaderProps {
    file: File;
    projectName?: string;
    shortName?: string;
    templateStandard?: string;
    existingProjectId?: string;
    onComplete: (projectId: string) => void;
    onError: (error: string, projectId?: string) => void;
}

interface AnalysisInsight {
    id: string;
    text: string;
    icon: 'search' | 'check' | 'brain';
    delay: number;
}

export default function ProtocolUploader({ file, projectName, shortName, templateStandard, existingProjectId, onComplete, onError }: ProtocolUploaderProps) {
    const { user, loading: authLoading } = useAuth();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [processingStage, setProcessingStage] = useState<'uploading' | 'analyzing' | 'generating' | 'complete'>('uploading');
    const [statusText, setStatusText] = useState('Initializing upload...');
    const [visibleInsights, setVisibleInsights] = useState<AnalysisInsight[]>([]);
    const [studySetup, setStudySetup] = useState<Partial<StudySetup>>(DEFAULT_STUDY_SETUP);
    const [analysisComplete, setAnalysisComplete] = useState(false);
    const [highlightFields, setHighlightFields] = useState<string[]>([]);

    // Use refs to prevent state update on unmounted component
    const mounted = useRef(true);
    const insightsEndRef = useRef<HTMLDivElement>(null);
    const studySetupRef = useRef(studySetup);
    const projectIdRef = useRef<string | null>(null);

    useEffect(() => { studySetupRef.current = studySetup; }, [studySetup]);

    const ANALYSIS_INSIGHTS: AnalysisInsight[] = [
        { id: '1', text: 'Reading protocol document...', icon: 'search', delay: 2000 },
        { id: '2', text: 'Extracting study design and objectives...', icon: 'search', delay: 15000 },
        { id: '3', text: 'Identifying endpoints and procedures...', icon: 'search', delay: 35000 },
        { id: '4', text: 'Analyzing inclusion/exclusion criteria...', icon: 'search', delay: 55000 },
        { id: '5', text: 'Building visit schedule...', icon: 'search', delay: 80000 },
        { id: '6', text: 'Verifying extraction completeness...', icon: 'search', delay: 110000 },
        { id: '7', text: 'Generating study artifacts...', icon: 'search', delay: 140000 },
        { id: '8', text: 'Building CRF forms...', icon: 'search', delay: 170000 },
    ];

    useEffect(() => {
        return () => { mounted.current = false; };
    }, []);

    // Auto-scroll insights list when new items appear
    useEffect(() => {
        insightsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [visibleInsights]);

    // Insight simulation effect
    useEffect(() => {
        if (processingStage === 'analyzing') {
            ANALYSIS_INSIGHTS.forEach(insight => {
                setTimeout(() => {
                    if (mounted.current) {
                        setVisibleInsights(prev => [...prev, insight]);
                    }
                }, insight.delay);
            });
        }
    }, [processingStage]);

    const finishAndNavigate = async () => {
        if (projectIdRef.current) {
            try {
                await updateDoc(projectDoc(projectIdRef.current), {
                    // Strip undefined values, which Firestore rejects (a cleared
                    // field, or Confirm countries, sets one), as StudySetupPanel does.
                    studySetup: JSON.parse(JSON.stringify(studySetupRef.current)),
                    // A Study Settings write: move its version on (saveStudySettings.ts).
                    studySettingsVersion: increment(1),
                });
            } catch (err) {
                console.error('Failed to save study setup:', err);
            }
            onComplete(projectIdRef.current);
        }
    };

    const uploadAndAnalyze = async () => {
        let projectRef: any = null;
        // Only a project THIS flow minted is ever discarded, and only when the
        // reading never started (below). A re-upload into an existing project
        // is never deleted.
        let createdNewProject = false;
        // True once the reading itself has started: only a failure after that
        // point is worded as "the protocol could not be read".
        let readingStarted = false;

        console.log('[ProtocolUploader] Starting uploadAndAnalyze');
        console.log('[ProtocolUploader] Props:', { projectName, shortName, fileName: file.name, existingProjectId });

        try {
            if (authLoading) throw new Error("Authentication not ready. Please wait.");
            if (!user) throw new Error("You must be logged in to create a project.");

            // 1. Mint the project BEFORE requesting the signed upload URL.
            //    The signed-URL endpoint requires `projectId` so it can verify
            //    the caller has the `upload` privilege on that project.
            if (existingProjectId) {
                projectRef = projectDoc(existingProjectId);
            } else {
                const finalName = projectName || file.name.replace(/\.pdf$/i, '').replace(/_/g, ' ');

                // Create through the callable, never with a direct setDoc.
                // The client cannot enforce a required, sanitized, unique
                // Short Name, and it cannot enforce the per-account project
                // limit — this path used to bypass both, because
                // firestore.rules permits any signed-in user to create a
                // project document. The callable also mints the owner member
                // row, so the self-bootstrap write that used to follow is
                // gone too.
                const createProjectFn = httpsCallable<
                    Record<string, unknown>,
                    { projectId: string }
                >(functions, CLOUD_FUNCTIONS.CREATE_PROJECT, { timeout: CALLABLE_TIMEOUTS.SHORT });
                const created = await createProjectFn({
                    name: finalName,
                    shortName,
                    description: 'Protocol uploaded via wizard',
                    studyType: 'interventional',
                    templateStandard: templateStandard || 'general',
                });
                projectRef = projectDoc(created.data.projectId);
                createdNewProject = true;
            }
            projectIdRef.current = projectRef.id;

            // 2. Get Upload URL — requires Firebase ID token + projectId.
            setStatusText('Securing upload channel...');
            const API_BASE = functionsBase();
            const idToken = await user.getIdToken();

            // getUploadUrlCore: same endpoint logic, served from the lean
            // `core` functions codebase so upload-URL minting no longer loads
            // the whole app bundle at cold start (2026-07-07 OOM outage).
            const response = await fetch(`${API_BASE}/getUploadUrlCore`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                    projectId: projectRef.id,
                    fileName: file.name,
                    storagePath: 'protocols',
                    contentType: 'application/pdf',
                }),
            });

            if (!response.ok) {
                // Surface the server's actual error (e.g. an unsupported file
                // type or an invalid resolved path) instead of a generic message
                // that masks every non-2xx as the same string.
                const serverError = await response.json().then(b => b?.error).catch(() => null);
                throw new Error(serverError || `Failed to get upload URL (HTTP ${response.status})`);
            }
            const data = await response.json();

            // 3. Upload File
            setStatusText('Uploading protocol document...');
            const uploadResponse = await fetch(data.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': 'application/pdf' },
            });

            if (!uploadResponse.ok) throw new Error('Failed to upload file');
            setUploadProgress(100);

            // 4. Stamp protocolFilePath on the project so downstream phases
            //    can find the uploaded protocol.
            setProcessingStage('analyzing');
            setStatusText('Analyzing protocol structure...');
            await updateDoc(projectRef, {
                protocolFilePath: data.filePath,
                status: 'setup',
                updatedAt: serverTimestamp(),
            });

            readingStarted = true;

            // 4. Analysis & Generation (Parallel with visual simulation)
            const MIN_VISUAL_TIME = 8000; // Force 8 seconds of "wow"

            const analysisPromise = (async () => {
                let analysisData: any = null;

                try {
                    // Trigger Analysis (now requires Firebase ID token).
                    const analysisResponse = await fetch(`${API_BASE}/analyzeProtocolV2`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${idToken}`,
                        },
                        body: JSON.stringify({
                            projectId: projectRef.id,
                            filePath: data.filePath
                        })
                    });

                    if (!analysisResponse.ok) {
                        const errorText = await analysisResponse.text();
                        // The server sends a message written for a person in `error`.
                        // Showing the raw body instead put a JSON blob in front of the
                        // user on 2026-08-31 and told them nothing they could act on.
                        let message = errorText;
                        try {
                            const parsed = JSON.parse(errorText);
                            if (parsed?.error) message = String(parsed.error);
                        } catch {
                            // Not JSON - fall back to the raw text rather than hiding it.
                        }
                        throw new Error(message);
                    }

                    analysisData = await analysisResponse.json();
                } catch (fetchErr: any) {
                    const msg = String(fetchErr?.message || '');
                    const isNetworkDisconnect =
                        msg === 'Failed to fetch' ||
                        msg.includes('NetworkError') ||
                        msg.includes('ERR_CONNECTION') ||
                        fetchErr instanceof TypeError;

                    // If it was an explicit server response (HTTP 4xx/5xx with user message), rethrow.
                    if (!isNetworkDisconnect) {
                        throw fetchErr;
                    }

                    console.warn('[ProtocolUploader] Direct HTTP connection closed while analysis was in flight; polling Firestore for completion...', fetchErr);
                    setStatusText('Analysis in progress on server, awaiting completion...');

                    // The backend Cloud Function continues running in the cloud. Poll Firestore for completion:
                    const pollStart = Date.now();
                    const MAX_POLL_MS = 300000; // 5 minutes
                    while (Date.now() - pollStart < MAX_POLL_MS) {
                        await new Promise(r => setTimeout(r, 4000));
                        if (!mounted.current) return;

                        try {
                            const snap = await getDoc(doc(db, 'projects', projectRef.id, 'protocolAnalysis', 'latest'));
                            if (snap.exists()) {
                                const latest = snap.data();
                                if (latest?.status === 'extraction_failed') {
                                    throw new Error(latest.error?.userMessage || 'Protocol analysis did not complete.');
                                }
                                if (latest?.analysis) {
                                    console.log('[ProtocolUploader] Recovered completed analysis from Firestore snapshot.');
                                    analysisData = { status: 'completed', analysis: latest.analysis };
                                    break;
                                }
                            }
                        } catch (pollErr: any) {
                            if (pollErr?.message && pollErr.message !== 'Failed to fetch') throw pollErr;
                        }
                    }

                    if (!analysisData) {
                        throw new Error('Analysis timed out. Please refresh your dashboard to inspect study status.');
                    }
                }

                if (analysisData.status !== 'completed') {
                    throw new Error(`Analysis failed to complete. Status: ${analysisData.status}`);
                }

                // Auto-populate study setup from analysis
                const autoUpdates: Partial<StudySetup> = {};
                const highlighted: string[] = [];

                if (!studySetupRef.current.sponsor && analysisData.analysis?.contacts?.sponsor) {
                    autoUpdates.sponsor = analysisData.analysis.contacts.sponsor;
                    highlighted.push('sponsor');
                }
                if (studySetupRef.current.hasDSMB === 'unknown' && analysisData.analysis?.safety?.dsmb !== undefined) {
                    autoUpdates.hasDSMB = analysisData.analysis.safety.dsmb ? 'yes' : 'no';
                    highlighted.push('hasDSMB');
                }
                if (!studySetupRef.current.irbOfRecord && analysisData.analysis?.contacts?.irbName && analysisData.analysis.contacts.irbName !== 'Not specified') {
                    autoUpdates.irbOfRecord = analysisData.analysis.contacts.irbName;
                    highlighted.push('irbOfRecord');
                }
                if (!studySetupRef.current.indIdeNumber && analysisData.analysis?.ideOrIndNumber && analysisData.analysis.ideOrIndNumber !== 'Not specified') {
                    autoUpdates.indIdeNumber = analysisData.analysis.ideOrIndNumber;
                    highlighted.push('indIdeNumber');
                }

                // Protocol number: ONLY the protocol's own extracted protocol
                // number, through the refusal rule (utils/protocolNumberRule.ts,
                // a mirror of the functions rule). Until 2026-10-01 this copied
                // the IND/IDE number into the protocol number, the root cause
                // of documents printing an IND number as the protocol number.
                const protoNum = autoFilledProtocolNumber(analysisData.analysis);
                if (!studySetupRef.current.protocolNumber && protoNum) {
                    autoUpdates.protocolNumber = protoNum;
                    highlighted.push('protocolNumber');
                }

                // Countries: only when none are entered yet (never
                // overwritten), marked as from the protocol for the user to
                // confirm (utils/protocolCountriesRule.ts, a mirror of the
                // functions rule). An empty extracted list means unknown, so
                // nothing is filled from it.
                const countryFill = countriesPrefill(studySetupRef.current, analysisData.analysis);
                if (countryFill.countries || countryFill.countriesUnmapped) {
                    Object.assign(autoUpdates, countryFill);
                    highlighted.push('countries');
                }

                // Therapeutic area
                if (!studySetupRef.current.therapeuticArea && analysisData.analysis?.therapeuticArea &&
                    analysisData.analysis.therapeuticArea !== 'Not specified') {
                    autoUpdates.therapeuticArea = analysisData.analysis.therapeuticArea;
                    highlighted.push('therapeuticArea');
                }

                // Primary objective (join array into single string)
                if (!studySetupRef.current.primaryObjective && analysisData.analysis?.objectives?.primary?.length > 0) {
                    autoUpdates.primaryObjective = analysisData.analysis.objectives.primary.join('; ');
                    highlighted.push('primaryObjective');
                }

                // Sub-investigators (join co-PIs from studyCenters)
                if (!studySetupRef.current.subInvestigators) {
                    const coPis = (analysisData.analysis?.studyCenters || [])
                        .map((c: any) => c.coPi)
                        .filter((c: string) => c && c !== 'Not specified' && c !== 'N/A');
                    if (coPis.length > 0) {
                        autoUpdates.subInvestigators = coPis.join('; ');
                        highlighted.push('subInvestigators');
                    }
                }

                // Funding agency (separate from sponsor — use 'other' + text)
                if ((!studySetupRef.current.fundingAgency || studySetupRef.current.fundingAgency === 'unknown') &&
                    analysisData.analysis?.fundingAgency &&
                    analysisData.analysis.fundingAgency !== 'Not specified') {
                    autoUpdates.fundingAgency = 'other';
                    autoUpdates.fundingAgencyOther = analysisData.analysis.fundingAgency;
                    highlighted.push('fundingAgency');
                }

                if (Object.keys(autoUpdates).length > 0) {
                    setStudySetup(prev => ({ ...prev, ...autoUpdates }));
                    setHighlightFields(highlighted);
                    setTimeout(() => setHighlightFields([]), 3000);
                }

                // Retry helper
                const generateWithRetry = async (type: string, retries = 3) => {
                    for (let i = 0; i < retries; i++) {
                        try {
                            const idToken = await user!.getIdToken();
                            const res = await fetch(`${API_BASE}/generateArtifactDemoV2`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${idToken}`,
                                },
                                // userId is intentionally omitted — the server
                                // derives the audit identity from the verified token.
                                body: JSON.stringify({ projectId: projectRef.id, artifactType: type }),
                            });
                            const data = await res.json();
                            if (res.ok && data.status === 'completed') return;
                            console.warn(`Attempt ${i + 1} failed for ${type}:`, data);
                            if (data.error) throw new Error(data.error);
                        } catch (e) {
                            console.warn(`Attempt ${i + 1} error for ${type}:`, e);
                        }
                        // Wait 2s before retry
                        await new Promise(r => setTimeout(r, 2000));
                    }
                    console.error(`Failed to generate ${type} after ${retries} attempts`);
                };

                // Generate Artifacts sequentially after analysis succeeds
                setProcessingStage('generating');
                setStatusText('Synthesizing study artifacts...');

                // Run sequentially to reduce load/contention.
                //
                // NOTE: We intentionally do NOT call generateWithRetry('crf')
                // here. The legacy CRFGenerator path seeds ~8 skeleton forms
                // from the visit schedule and writes them as `artifacts/crf-*`
                // — but the CRF Builder uses `suggestCRFForms` (LLM-based)
                // when it opens on a protocol with zero existing CRF
                // artifacts, producing 40–50 context-aware forms. If we seed
                // the 8-form skeleton first, the Builder opens in 'workspace'
                // phase with those 8 and never shows the Generate button for
                // the richer suggestion pass. See the same warning in
                // functions/src/generateAllFromProtocol.ts (lines 128–137).
                await generateWithRetry('consent_form');
            })();

            const visualPromise = new Promise(resolve => setTimeout(resolve, MIN_VISUAL_TIME));

            // Wait for both analysis AND the show
            await Promise.all([analysisPromise, visualPromise]);

            // 5. Complete
            setProcessingStage('complete');
            setStatusText('Project ready!');
            setAnalysisComplete(true);

        } catch (err: any) {
            console.error('Workflow error:', err);
            // Once the reading has started the study STAYS. Until 2026-10-04 this
            // handler hard-deleted it, which also removed the failure marker
            // the server had written, so a user who was not watching found no
            // study and no explanation. The study page shows the marker and
            // offers the retry. Before the reading starts (an upload failure)
            // no marker exists and the study would be an empty orphan, so the
            // project this upload created is discarded, as it always was.
            let discarded = false;
            if (projectRef && createdNewProject && !readingStarted) {
                try {
                    const discardFn = httpsCallable(functions, CLOUD_FUNCTIONS.DISCARD_FAILED_PROJECT, { timeout: CALLABLE_TIMEOUTS.LONG });
                    await discardFn({ projectId: projectRef.id });
                    discarded = true;
                } catch (cleanupErr) {
                    console.warn("Failed to cleanup partial project", cleanupErr);
                }
            }
            const reason = err?.message || 'Failed to process protocol';
            onError(readingStarted ? protocolReadFailureMessage(reason) : reason, discarded ? undefined : projectRef?.id);
        }
    };

    useEffect(() => {
        uploadAndAnalyze();
    }, []);

    return (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden relative border border-slate-200 flex flex-col" style={{ maxHeight: '90vh' }}>
                {/* Header background */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-primary-500 to-purple-500 animate-gradient" />

                {/* Two-column layout */}
                <div className="flex flex-1 min-h-0">
                    {/* Left column - Progress */}
                    <div className="w-2/5 p-8 flex flex-col items-center">
                        {/* Visual Stage Indicator */}
                        <div className="w-24 h-24 mb-6 relative flex items-center justify-center">
                            {processingStage === 'uploading' && (
                                <div className="relative">
                                    <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
                                    <div
                                        className="absolute inset-0 border-4 border-primary-500 rounded-full transition-all duration-300"
                                        style={{ clipPath: `inset(0 0 ${100 - uploadProgress}% 0)` }}
                                    />
                                    <Loader2 className="h-10 w-10 text-primary-600 animate-spin relative z-10" />
                                </div>
                            )}

                            {processingStage === 'analyzing' && (
                                <div className="relative">
                                    <div className="absolute inset-0 bg-primary-100 rounded-full animate-ping opacity-20" />
                                    <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center border border-primary-100">
                                        <BrainCircuit className="h-10 w-10 text-primary-600 animate-pulse" />
                                    </div>
                                    <div className="absolute -top-2 -right-2">
                                        <Sparkles className="h-6 w-6 text-yellow-500 animate-bounce" />
                                    </div>
                                </div>
                            )}

                            {processingStage === 'generating' && (
                                <div className="relative">
                                    <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center border border-purple-100">
                                        <FileSearch className="h-10 w-10 text-purple-600 animate-pulse" />
                                    </div>
                                </div>
                            )}

                            {processingStage === 'complete' && (
                                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center border border-green-100 scale-110 transition-transform">
                                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                                </div>
                            )}
                        </div>

                        <h3 className="text-xl font-bold text-slate-900 mb-1 text-center transition-all">
                            {statusText}
                        </h3>
                        {processingStage === 'analyzing' && (
                            <p className="text-sm text-slate-400 text-center mb-1">This may take several minutes depending on protocol length.</p>
                        )}

                        {/* Progress Bar */}
                        {processingStage !== 'complete' && (
                            <div className="w-full mt-4">
                                <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full transition-all duration-[2000ms] ease-out bg-gradient-to-r from-blue-500 via-primary-500 to-purple-500"
                                        style={{
                                            width: processingStage === 'uploading' ? `${Math.max(uploadProgress, 5)}%`
                                                : processingStage === 'analyzing' ? `${Math.min(10 + visibleInsights.length * 10, 90)}%`
                                                : '95%'
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-slate-400 text-right mt-1">
                                    {processingStage === 'uploading' ? `${uploadProgress}%`
                                        : processingStage === 'analyzing' ? `${Math.min(10 + visibleInsights.length * 10, 90)}%`
                                        : '95%'}
                                </p>
                            </div>
                        )}

                        {processingStage === 'complete' && (
                            <div className="w-full mt-4">
                                <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className="h-2 rounded-full bg-green-500 transition-all duration-500" style={{ width: '100%' }} />
                                </div>
                                <p className="text-xs text-green-600 text-right mt-1">100%</p>
                            </div>
                        )}

                        {/* Dynamic Insights List */}
                        <div className="w-full max-h-64 mt-6 space-y-3 relative overflow-y-auto">
                            <div className="sticky bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />

                            {visibleInsights.map((insight, idx) => (
                                <div
                                    key={insight.id}
                                    className="flex items-center space-x-3 text-sm p-3 bg-slate-50 rounded-xl border border-slate-100 animate-slide-up shadow-sm"
                                    style={{ animationDelay: `${idx * 100}ms` }}
                                >
                                    {insight.icon === 'search' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                                    {insight.icon === 'check' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                                    {insight.icon === 'brain' && <BrainCircuit className="w-4 h-4 text-primary-500" />}

                                    <span className="font-medium text-slate-700">{insight.text}</span>
                                </div>
                            ))}
                            <div ref={insightsEndRef} />
                        </div>

                        {!analysisComplete && (
                            <div className="mt-4 flex items-center space-x-2 text-xs text-slate-400">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>Reading the protocol</span>
                            </div>
                        )}
                    </div>

                    {/* Vertical divider */}
                    <div className="border-l border-gray-200" />

                    {/* Right column - Study Setup Form */}
                    <div className="w-3/5 p-8 flex flex-col min-h-0">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-slate-900">Study Configuration</h3>
                            <p className="text-sm text-slate-400 mt-0.5">Fill in what you know -- all fields are optional</p>
                        </div>
                        <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: '55vh' }}>
                            <StudySetupForm
                                value={studySetup}
                                onChange={setStudySetup}
                                highlightFields={highlightFields}
                                compact
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-gray-200 px-8 py-4 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={finishAndNavigate}
                        className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                        Skip for now
                    </button>
                    <div>
                        {analysisComplete ? (
                            <button
                                type="button"
                                onClick={finishAndNavigate}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                            >
                                Go to Project
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                disabled
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-400 text-sm font-medium rounded-lg cursor-not-allowed"
                            >
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Waiting for analysis...
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
